import {Button, Stack, Typography} from '@mui/material';
import {CTAButtonFixed, EmptySearchState, ErrorState, StyledGrid} from 'presentation/components';
import {
	FeaturesEnum,
	HealthGuideServicesTypeEnum,
	OdontoGuideServicesTypeEnum,
} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {PlanTypeEnum} from 'presentation/constants/bookingTypesEnum';
import {CustomCTAButtonFixedAction} from 'presentation/pages/private/business-access-permission/styles';
import {translations} from 'presentation/translations-new';
import React, {Suspense} from 'react';
import {isEmpty} from '../../../utils/isEmpty';
import {HealthSpecialtiesInput} from '../HealthSpecialtiesInput';
import {NetworkInput} from '../NetworkInput';
import {OdontoSpecialtiesInput} from '../OdontoSpecialtiesInput';
import {SkeletonInput} from '../SkeletonInput';
import {useSearchDetails} from './hooks/useSearchDetails';
import {OnDemandExamDrawer} from 'presentation/modules/booking/shared/ui/components/OnDemandExamDrawer';
import {SearchProcedure} from './SearchProcedure';
import {ServiceCards} from './ServiceCards';
import {Container, SelectWrapper} from './styles';
import {GuideSearchbarProps} from './types';
import {Loading} from 'presentation/components/Loading/Loading';
import {ExamOrientationsDialog} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages';
import {ExamInfo} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog/store/useExamOrientationsInfoStore/types';

const {seeResults, failToLoadTitle, failToLoadDescription} = translations['pt-br'].commons;
const {
	networkType,
	specialtyHealthType,
	specialtyOdontoType,
	loadingSpecialties,
	guideDetails: {emptyState},
} = translations['pt-br'].privateFlow.guideFlow;

export function SearchDetails({type, onSearchResults}: GuideSearchbarProps) {
	const {
		isSemanticSearchEnabled,
		isMobile,
		shouldShowNetworkFilter,
		services,
		servicesLoading,
		networkFilters,
		networkLoading,
		healthSpecialties,
		healthSpecialtiesLoading,
		odontoSpecialties,
		odontoSpecialtiesLoading,
		hasErrors,
		handleRetryFailedQueries,
		handleNavToStateSelection,
		healthCardIsLoading,
		specialtyCode,
		networkCode,
		serviceType,
		cd_procedimento,
		openOnDemandExamDrawer,
		handleCloseOnDemandExamDrawer,
		handleOpenOnDemandExamDrawer,
		handleSearchResults,
		isOnDemandExamLoading,
		specialtyName,
		hasExamPreparation,
		examsOrientationsModal,
		setExamOrientationsModal,
		handleViewOnDemandExamUnits,
	} = useSearchDetails(type);

	const setBookingData = useBookingStore(state => state.setBookingData);
	const {clearSearch} = useQueryParams();

	const handleAgreeExamOrientations = (examInfo: ExamInfo) => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: examInfo.code,
			specialtyName: examInfo.examName,
			procedureCode: examInfo.procedureCode,
			professionalCode: '',
			professionalName: '',
			unitCode: '',
			unitName: '',
			unitAddress: '',
			dateAt: '',
			hourAt: '',
		}));

		clearSearch();
		setExamOrientationsModal(false);
		onSearchResults();
	};

	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);

	if (services?.length === 0) {
		return (
			<Stack mt={isMobile ? 2 : 4} px={isMobile ? 2 : 4}>
				<EmptySearchState title={emptyState.title} description={emptyState.description}>
					<Button size="large" onClick={handleNavToStateSelection}>
						{emptyState.buttonLabel}
					</Button>
				</EmptySearchState>
			</Stack>
		);
	}

	if (hasErrors) {
		return (
			<ErrorState
				title={failToLoadTitle}
				description={failToLoadDescription}
				handleRefresh={handleRetryFailedQueries}
			/>
		);
	}

	const isMissingProcedure =
		isEmpty(cd_procedimento) && serviceType === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES;
	const isMissingSpecialty =
		isEmpty(specialtyCode) && serviceType === HealthGuideServicesTypeEnum.OUTPATIENT;

	const shouldDisableHealthButton =
		isEmpty(serviceType) ||
		(shouldShowNetworkFilter && isEmpty(networkCode)) ||
		isEmpty(services) ||
		(isSchedulingByMedicalGuideEnabled && (isMissingProcedure || isMissingSpecialty));

	const shouldDisableOdontoButton =
		isEmpty(services) ||
		isEmpty(serviceType) ||
		(serviceType === OdontoGuideServicesTypeEnum.OUTPATIENT && !specialtyCode);

	return (
		<Container>
			<Stack gap={3}>
				<ServiceCards type={type} services={services} isLoading={servicesLoading} />

				<StyledGrid.Root spacing={1}>
					{shouldShowNetworkFilter ? (
						<StyledGrid.Item xs={isMobile ? 12 : 6} sm={4} md={6} lg={4} xl={3}>
							<SelectWrapper>
								<Typography variant="body2" fontWeight={600} color="text.secondary">
									{networkType}
								</Typography>
								<NetworkInput
									networkFilters={networkFilters}
									isLoading={networkLoading}
								/>
							</SelectWrapper>
						</StyledGrid.Item>
					) : null}

					{type === PlanTypeEnum.HEALTH ? (
						<StyledGrid.Item xs={isMobile ? 12 : 6} sm={4} md={6} lg={4} xl={3}>
							{isSemanticSearchEnabled &&
							serviceType === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES ? (
								<SearchProcedure />
							) : (
								<SelectWrapper>
									<Typography
										variant="body2"
										fontWeight={600}
										color="text.secondary">
										{specialtyHealthType}
									</Typography>
									<Suspense
										fallback={<SkeletonInput label={loadingSpecialties} />}>
										<HealthSpecialtiesInput
											healthSpecialties={healthSpecialties}
											isLoading={
												healthSpecialtiesLoading || healthCardIsLoading
											}
										/>
									</Suspense>
								</SelectWrapper>
							)}
						</StyledGrid.Item>
					) : null}

					{type === PlanTypeEnum.ODONTO ? (
						<StyledGrid.Item xs={isMobile ? 12 : 6} sm={4} md={6} lg={4} xl={3}>
							<SelectWrapper>
								<Typography variant="body2" fontWeight={600} color="text.secondary">
									{specialtyOdontoType}
								</Typography>
								<Suspense fallback={<SkeletonInput label={loadingSpecialties} />}>
									<OdontoSpecialtiesInput
										odontoSpecialties={odontoSpecialties}
										isLoading={odontoSpecialtiesLoading}
									/>
								</Suspense>
							</SelectWrapper>
						</StyledGrid.Item>
					) : null}
				</StyledGrid.Root>
			</Stack>

			<CTAButtonFixed.Root direction="column" justifyContent="end" mt={isMobile ? 2 : 4}>
				<CustomCTAButtonFixedAction
					fullWidth={!!isMobile}
					onClick={() => handleSearchResults(onSearchResults)}
					size="large"
					variant="contained"
					disabled={
						isOnDemandExamLoading ||
						(type === PlanTypeEnum.ODONTO
							? shouldDisableOdontoButton
							: shouldDisableHealthButton)
					}
					endIcon={isOnDemandExamLoading ? <Loading size={16} color="inherit" /> : null}
					sx={{px: 7.5}}>
					{seeResults}
				</CustomCTAButtonFixedAction>
			</CTAButtonFixed.Root>

			<OnDemandExamDrawer
				open={openOnDemandExamDrawer}
				specialtyName={specialtyName}
				hasExamPreparation={hasExamPreparation}
				onOpen={handleOpenOnDemandExamDrawer}
				onClose={handleCloseOnDemandExamDrawer}
				onViewUnits={handleViewOnDemandExamUnits}
			/>

			<ExamOrientationsDialog
				open={examsOrientationsModal}
				setOpen={setExamOrientationsModal}
				handleAgree={handleAgreeExamOrientations}
			/>
		</Container>
	);
}
